import type { ReactElement, ReactNode } from 'react';
import type {
  TooltipProps as AriaTooltipProps,
  TooltipTriggerComponentProps,
} from 'react-aria-components';
import { Tooltip as TooltipComponent, TooltipTrigger } from 'react-aria-components';
import cx from 'clsx';
import { Button, type ButtonProps } from '../button/button';
import styles from './tooltip.module.css';

export interface TooltipProps extends TooltipTriggerComponentProps {
  ariaLabel?: string;
  children: ReactNode;
  label: string | ReactElement | ReactNode;
  placement?: AriaTooltipProps['placement'];
  className?: string;
  tooltipTestId?: string; // Custom test ID for the tooltip
  needsButton?: boolean;
  isDisabled?: boolean;
  buttonProps?: Omit<ButtonProps, 'children'>;
  offset?: number;
  crossOffset?: number;
}

export function Tooltip({
  ariaLabel,
  children,
  label,
  placement = 'bottom',
  delay = 0,
  closeDelay = 0,
  className,
  tooltipTestId,
  needsButton = false,
  isDisabled = false,
  offset = 8,
  crossOffset,
  buttonProps,
  ...triggerProps
}: TooltipProps): JSX.Element {
  // react-aria modal escapes the test container, we render it where it can find the tooltip (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;

  return (
    <TooltipTrigger
      closeDelay={closeDelay}
      data-test-tooltip
      data-testid="tooltip"
      delay={delay}
      isDisabled={isDisabled}
      {...triggerProps}
    >
      {needsButton ? (
        <Button
          className={styles.button}
          data-test-tooltip-button={tooltipTestId}
          data-tooltip-wrapper={tooltipTestId}
          {...buttonProps}
        >
          {children}
        </Button>
      ) : (
        children
      )}
      <TooltipComponent
        UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
        aria-label={ariaLabel}
        className={cx(styles.content, 'body-standard', className)}
        crossOffset={crossOffset}
        data-test-tooltip-message
        data-testid={tooltipTestId}
        offset={offset}
        placement={placement}
      >
        {label}
      </TooltipComponent>
    </TooltipTrigger>
  );
}
